import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, Building2, Calendar, Clock, Info, Pencil, X } from 'lucide-react';
import { Appointment } from '../types';
import { getTotalDuration, getTotalPrice } from '../utils/appointmentHelpers';
import { apiService } from '../services/api';

interface RebookConfirmModalProps {
  isOpen: boolean;
  appointment: Appointment | null;
  onClose: () => void;
  onEditServices: () => void;
  onContinue: () => void;
}

function formatMoney(amount: number): string {
  const value = Number.isFinite(amount) ? amount : 0;
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
}

function formatApptDate(d: Date): string {
  return d.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}

function formatTime(d: Date): string {
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}

export const RebookConfirmModal: React.FC<RebookConfirmModalProps> = ({
  isOpen,
  appointment,
  onClose,
  onEditServices,
  onContinue
}) => {
  const appt = appointment;
  const [deletedServiceIds, setDeletedServiceIds] = useState<Set<string>>(new Set());

  const { dateLabel, timeRangeLabel, businessName, lines, estimatedTotal } = useMemo(() => {
    if (!appt) {
      return {
        businessName: '',
        dateLabel: '',
        timeRangeLabel: '',
        lines: [] as Array<{ serviceId: string; serviceName: string; professionalName: string; price: number }>,
        estimatedTotal: 0
      };
    }

    const startIso = (appt as any).scheduledAt ?? (appt as any).flexScheduledAt?.earliest;
    const start = startIso ? new Date(startIso) : new Date();
    const durationMins = getTotalDuration(appt) || 0;
    const end = new Date(start.getTime() + durationMins * 60000);

    const mapped = (Array.isArray(appt.assignments) ? appt.assignments : []).map((a) => {
      const professionalName =
        a?.professionalFirstName && a?.professionalLastName
          ? `${a.professionalFirstName} ${a.professionalLastName.charAt(0)}.`
          : 'Any professional';

      // Prefer the assignment price we already use for totals; fall back to servicePrice if needed.
      const price = Number(a?.price) || Number(a?.servicePrice) || 0;

      return {
        serviceId: String(a?.serviceId || ''),
        serviceName: a?.serviceName || 'Service',
        professionalName,
        price
      };
    });

    return {
      businessName: appt.business_name || 'Appointment',
      dateLabel: formatApptDate(start),
      timeRangeLabel: durationMins > 0 ? `${formatTime(start)} – ${formatTime(end)}` : formatTime(start),
      lines: mapped,
      estimatedTotal: getTotalPrice(appt)
    };
  }, [appt]);

  // When the modal opens, fetch current business services and detect any soft-deleted services.
  // UI-only logic: if a previously-booked service is now deleted=true, warn and block Continue.
  useEffect(() => {
    if (!isOpen || !appt) {
      setDeletedServiceIds(new Set());
      return;
    }
    const shortName = (appt as any)?.business_short_name || (appt as any)?.businessShortName;
    const assignments = Array.isArray((appt as any)?.assignments) ? (appt as any).assignments : [];
    const ids: string[] = assignments
      .map((a: any) => String(a?.serviceId || a?.service_id || ''))
      .filter(Boolean);
    if (!shortName || ids.length === 0) {
      setDeletedServiceIds(new Set());
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const services = await apiService.getBusinessServicesByShortName(String(shortName));
        const deleted = new Set<string>();
        const byId = new Map<string, any>((services || []).map((s: any) => [String(s?.id || ''), s]));
        ids.forEach((id: string) => {
          const svc = byId.get(id);
          if (svc && (svc as any).deleted === true) deleted.add(id);
        });
        if (!cancelled) setDeletedServiceIds(deleted);
      } catch {
        // If we can't verify, don't block the user.
        if (!cancelled) setDeletedServiceIds(new Set());
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, appt]);

  const hasDeletedServices = deletedServiceIds.size > 0;

  // Close on ESC
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen || !appointment) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden max-h-[80vh] sm:max-h-[calc(100vh-4rem)] flex flex-col">
          {/* Header */}
          <div className="flex items-start justify-between p-4 sm:p-6 border-b border-gray-200">
            <div className="min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <h2 className="text-xl font-bold text-gray-900">Confirm Rebook</h2>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">
                  Uses last appointment details
                </span>
              </div>
              <p className="text-sm text-gray-600 mt-2">
                This will take you to <span className="font-semibold text-gray-900">Select Professional</span>. Review what will be rebooked below.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors p-2 rounded-lg hover:bg-gray-100"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Body */}
          <div className="p-4 sm:p-6 space-y-5 overflow-y-auto flex-1">
            {hasDeletedServices && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 mt-0.5 text-amber-700" />
                  <div className="text-sm">
                    One or more services from your last appointment are no longer available. Please click{' '}
                    <span className="font-semibold">Edit Services</span> to remove/replace them before continuing.
                  </div>
                </div>
              </div>
            )}

            {/* Appointment meta */}
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-gray-700">
              <div className="flex items-center gap-2 min-w-0 w-full sm:w-auto">
                <Building2 className="w-4 h-4 text-gray-500 flex-shrink-0" />
                <span className="font-semibold text-gray-900 truncate">{businessName}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-500" />
                <span>{dateLabel}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-gray-500" />
                <span>{timeRangeLabel}</span>
              </div>
            </div>

            {/* Services */}
            <div className="rounded-2xl border border-gray-200 overflow-hidden">
              <div className="flex items-center justify-between px-5 py-4 bg-gray-50">
                <h3 className="text-base font-bold text-gray-900">Services &amp; Professionals</h3>
                <span className="text-sm text-gray-500">Prices are estimates</span>
              </div>

              <div className="divide-y divide-gray-200">
                {lines.map((line, idx) => (
                  <div key={`${line.serviceName}-${idx}`} className="px-5 py-4 flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-gray-900 font-semibold">{line.serviceName}</div>
                          {line.serviceId && deletedServiceIds.has(String(line.serviceId)) && (
                            <div className="mt-1 flex sm:hidden items-center gap-1 text-amber-700 text-xs font-semibold">
                              <AlertTriangle className="w-4 h-4" />
                              No longer available
                            </div>
                          )}
                        </div>
                        {line.serviceId && deletedServiceIds.has(String(line.serviceId)) && (
                          <div className="hidden sm:flex items-center gap-1 text-amber-700 text-xs font-semibold whitespace-nowrap">
                            <AlertTriangle className="w-4 h-4" />
                            No longer available
                          </div>
                        )}
                      </div>
                      <div className="text-sm text-gray-600 mt-1">
                        <span className="text-gray-500">Professional:</span> {line.professionalName}
                      </div>
                    </div>
                    <div className="text-gray-900 font-semibold whitespace-nowrap">{formatMoney(line.price)}</div>
                  </div>
                ))}
              </div>

              <div className="px-5 py-4 bg-gray-50 border-t border-gray-200">
                <div className="flex items-start justify-between gap-4">
                  <div className="space-y-3">
                    <div className="flex items-start gap-2 text-sm text-gray-700">
                      <Info className="w-4 h-4 text-gray-500 mt-0.5" />
                      <span>You can change services/professionals later if needed.</span>
                    </div>
                    <div className="flex items-start gap-2 text-sm text-gray-700">
                      <Info className="w-4 h-4 text-gray-500 mt-0.5" />
                      <span>Prices may change based on the final services provided.</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-gray-500">Estimated Total</div>
                    <div className="text-2xl font-extrabold text-gray-900">{formatMoney(estimatedTotal)}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-4 sm:p-6 border-t border-gray-200 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
            <button
              type="button"
              onClick={onClose}
              className="aacore-btn2-outline bg-gray-100 hover:bg-gray-200 rounded-full justify-center"
            >
              <X className="w-4 h-4" />
              Cancel
            </button>
            <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
              <button
                type="button"
                onClick={onEditServices}
                className="aacore-btn2-outline rounded-full justify-center"
              >
                <Pencil className="w-4 h-4" />
                Edit Services
              </button>
              <button
                type="button"
                onClick={onContinue}
                disabled={hasDeletedServices}
                className="aacore-btn2-primary rounded-full justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Calendar className="w-4 h-4" />
                Continue
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

